/**
 * Shared cube-health presentation (CSAGE-543).
 *
 * The diagnosis engine lives server-side in cube-feed-diagnostics.ts and is
 * surfaced by GET /cubes/health. Two viewer surfaces render it — the customer
 * data-status panel (DataStatusPanel) and the warehouse ops console
 * (admin/CubesAdminPage) — so the badge styling, the labels, and the
 * "does this cube need a human right now?" rule live in one place and can
 * never drift between them.
 */

import type { CubeDiagnosis, CubeHealthRow, FeedLifecycle } from '../api/cubes.js';

/**
 * Diagnosis → badge class, label, and a sort rank (lower floats to the top).
 * Problems first: a failing build, then a stalled upstream feed, then the
 * benign states. `not_checked` is a snapshot cube with no time dimension, so
 * there is nothing to be stale about.
 */
export const DIAGNOSIS_META: Record<CubeDiagnosis, { cls: string; label: string; rank: number }> = {
  build_failing: { cls: 'badge--danger', label: 'Build failing', rank: 0 },
  source_stalled: { cls: 'badge--warning', label: 'Feed stalled', rank: 1 },
  never_built: { cls: 'badge--neutral', label: 'Never built', rank: 2 },
  ok: { cls: 'badge--success', label: 'OK', rank: 3 },
  not_checked: { cls: 'badge--neutral', label: 'Snapshot', rank: 4 },
};

export const LIFECYCLE_META: Record<FeedLifecycle, { cls: string; label: string }> = {
  active: { cls: 'badge--success', label: 'active' },
  migrating: { cls: 'badge--info', label: 'migrating' },
  legacy: { cls: 'badge--warning', label: 'legacy' },
  decommissioned: { cls: 'badge--neutral', label: 'retired' },
};

/**
 * Whether a cube warrants operator attention — the same predicate the Phase 2
 * monitor uses to decide whether to alert.
 *
 * A failing build always counts. A stalled feed counts UNLESS the feed is
 * retired on purpose: the EJS-migration cubes (CSAGE-470/478, CA-2892) build
 * fine but cap out by design, so their staleness is expected, not an incident.
 * We key the suppression on the curated registry's `decommissioned` lifecycle
 * rather than on the diagnosis alone, so a genuinely broken feed still surfaces.
 */
export function cubeNeedsAttention(row: Pick<CubeHealthRow, 'diagnosis' | 'feed'>): boolean {
  if (row.diagnosis === 'build_failing') return true;
  if (row.diagnosis === 'source_stalled') return row.feed?.lifecycle !== 'decommissioned';
  return false;
}
